import {
  FontSizes,
  IButtonStyles,
  mergeStyles,
  PrimaryButton,
  Stack,
  Text,
} from "@fluentui/react";
import React, { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Project } from "../../models";
import Loader from "../../components/Loader";
import { ProjectInCard } from "../../components/Cards/ProjectInCard";
import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import { getAllProjects, getSearchedProjects } from "../redux/Projects/project";
import { colorPallete } from "../../assets/color";
import "./index.css";
import { useSelector } from "react-redux";
import PrevArrow from "../../assets/Icons/svg/prevArrow";
import NextArrow from "../../assets/Icons/svg/nextArrow";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

const retryButtonStyles: IButtonStyles = {
  root: {
    width: "20px",
    margin: "1%",
    alignItems: "center",
  },
};

const styles = {
  ProjectStackStyle: {
    width: "98%",
    height: "98%",
    alignItems: "flex-start",
    backgroundColor: colorPallete.white,
    flexDirection: "row",
  },
  TextDivStyle: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    width: "100%",
  },
  errorStyles: {
    display: "flex",
    justifyContent: "center",
    width: "90%",
    height: "18rem",
    alignItems: "center",
  },
  textStyles: {
    fontSize: FontSizes.size18,
    fontFamily: "Poppins",
  },
  loaderStyles: {
    display: "flex",
    justifyContent: "center",
    width: "90%",
    height: "18rem",
  },
  stackStyles: {
    display: "flex",
    justifyContent: "center",
    width: "100%",
    alignItems: "center",
    height: "100%",
    overflowX: "hidden",
    backgroundColor: "",
  },
};

export const Projects: FC<any> = (props) => {
  const [isError, setIsError] = useState<boolean>(false);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const isSearchEnabled = useAppSelector(
    (state) => state.isSearchEnabled.value
  );
  const searchParam = useAppSelector((state) => state.isSearchEnabled.text);
  const [projectData, setProjectData] = useState<Project[]>([]);
  const { isLoading, data, error, metadata } = useAppSelector((state) =>
    isSearchEnabled ? state.fetchSearchedProject : state.fetchProjects
  );
  const element = document.getElementById("myStack");

  const [currentSlide, setCurrentSlide] = useState(0);
  
  // Calculate slidesToShow based on screen width
  const getSlidesToShow = () => {
    if (screenWidth <= 480) return 1;
    if (screenWidth <= 1065) return 2;
    if (screenWidth <= 1325) return 3;
    return 4;
  };
  
  const slidesToShow = getSlidesToShow();
  const slidesToScroll = 1;
  const showPrevArrow = currentSlide > 0;
  const showNextArrow = projectData.length > slidesToShow && (currentSlide + slidesToShow) < projectData.length;

const settings = {
  dots: false,
  infinite: false,
  speed: 500,
  slidesToShow,
  slidesToScroll: slidesToScroll,
  arrows: true,
  afterChange: (index: number) => setCurrentSlide(index),
  prevArrow: showPrevArrow ? <PrevArrow className="prevArrow" /> : undefined,
  nextArrow: showNextArrow ? <NextArrow className="nextArrow" /> : undefined,
  responsive: [
    {
      breakpoint: 1325,
      settings: {
        slidesToShow: 3,
        slidesToScroll: 1,
      }
    },
    {
      breakpoint: 1065,
      settings: {
        slidesToShow: 2,
        slidesToScroll: 1,
      }
    },
    {
      breakpoint: 480,
      settings: {
        slidesToShow: 1,
        slidesToScroll: 1,
      }
    }
  ]
};

  useEffect(() => {
    const handleResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    let width = screenWidth <= 1350 ? screenWidth : 1350;
    if (data && data.length * 270 + 260 > width) {
      props?.setArrowButtons(true);
    } else {
      props?.setArrowButtons(false);
    }
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [data, screenWidth]);

  useEffect(() => {
    if (data && data.length > 0) {
      setProjectData(data);
    } else {
      setProjectData([]);
    }
  }, [data, metadata]);

  useEffect(() => {
    isSearchEnabled &&
      dispatch(
        getSearchedProjects({
          projectName: searchParam,
          limit: 120,
          offset: 0,
        })
      );
  }, [searchParam]);

  useEffect(() => {
    if (data.length === 0 || isError)
      dispatch(
        getAllProjects({
          limit: 500,
          offset: 0,
        })
      );
  }, [isError]);

  useEffect(() => {

    if (props.left && element) {
      element.scrollLeft += props.left;
    }

    // Check if scroll is at the start or end
    if (element) {
      const isAtStart = element.scrollLeft === 0;
      const isAtEnd =
        element.scrollLeft + element.clientWidth === element.scrollWidth;

      props.setLeftArrowVisible(!isAtStart); // Hide left arrow if at the start
      props.setRightArrowVisible(!isAtEnd); // Hide right arrow if at the end
    }
  }, [props.left, projectData]);

  const loaderJSX = useMemo(() => {
    return isLoading ? (
      <Stack className={mergeStyles(styles.loaderStyles)}>
        <Loader />
      </Stack>
    ) : null;
  }, [isLoading]);

  const errorJSX = !isLoading && error !== "" && (
    <Stack className={mergeStyles(styles.errorStyles)}>
      <Text className={mergeStyles(styles.textStyles)}>{t("loader")}</Text>
      <PrimaryButton
        styles={retryButtonStyles}
        onClick={() => setIsError(!isError)}
      >
        {t("retry")}
      </PrimaryButton>
    </Stack>
  );

  return (
    projectData && (
      <div className="Projects" id="myStack">
        <div style={{ width: "100%", overflow: "hidden" }}>
          {loaderJSX}
          {errorJSX}
          {!isLoading && data.length !== 0 && (
            <Slider key={`slider-${props.selectedId}-${projectData.length}`}
              {...settings}>
              {projectData.map((project, i) => (
                <div 
                  key={project.id}
                  style={{
                    padding: "0 10px",
                    boxSizing: "border-box",
                    width: "100%",
                  }}>
                  <ProjectInCard
                    project={project}
                    disableValueEdit
                  />
                </div>
              ))}
            </Slider>
          )}
          
            {!isLoading && data.length === 0 && !errorJSX && (
              <div style={styles.TextDivStyle}>
                <Text className={mergeStyles(styles.textStyles)}>
                  {t("no_projects_found")}
                </Text>
              </div>
            )}
        </div>
      </div>
    )
  );
};
